import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'

const AuthContext = createContext()

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profile, setProfile] = useState(null)
  const isAdmin = !!profile?.is_admin || profile?.role === 'admin'
  const isOwner = profile?.role === 'owner'
  const canViewHidden = isAdmin || isOwner
  
  const signupEndpoint = process.env.VITE_SIGNUP_ENDPOINT || '/api/auth/signup'
  const loginEndpoint = process.env.VITE_LOGIN_ENDPOINT || signupEndpoint.replace(/\/signup(?=$|\?.*)/, '/login')
  const meEndpoint = process.env.VITE_ME_ENDPOINT || signupEndpoint.replace(/\/signup(?=$|\?.*)/, '/me')
  const customAuth = true

  useEffect(() => {
    let mounted = true
    const init = async () => {
      setLoading(true)
      if (customAuth) {
        const token = localStorage.getItem('auth_token')
        if (token && meEndpoint) {
          try {
            const resp = await fetch(meEndpoint, { headers: { Authorization: `Bearer ${token}` } })
            if (resp.ok) {
              const json = await resp.json()
              setUser({ id: json.user.id, email: json.user.email })
              setProfile(json.user)
            } else {
              setUser(null); setProfile(null)
            }
          } catch {
            setUser(null); setProfile(null)
          }
        } else {
          setUser(null); setProfile(null)
        }
      } else {
        console.log('[Auth] init getSession...')
        const { data: { session }, error: sessErr } = await supabase.auth.getSession()
        if (sessErr) console.error('[Auth] getSession error', sessErr)
        if (mounted) {
          const u = session?.user || null
          console.log('[Auth] init session user =', !!u ? u.id : null)
          setUser(u)
          if (u) {
            const { data: p, error: profErr } = await supabase
              .from('profiles')
              .select('id,email,is_admin,role,phone')
              .eq('id', u.id)
              .maybeSingle()
            if (profErr) console.error('[Auth] profiles fetch error', profErr)
            setProfile(p || null)
          } else {
            setProfile(null)
          }
        }
      }
      setLoading(false)
    }
    init()
    let sub
    if (!customAuth) {
      const r = supabase.auth.onAuthStateChange(async (event, session) => {
        console.log('[Auth] onAuthStateChange', event)
        const u = session?.user || null
        setUser(u)
        if (u) {
          const { error: upErr } = await supabase
            .from('profiles')
            .upsert({ id: u.id, email: u.email }, { onConflict: 'id' })
          if (upErr) console.error('[Auth] profiles upsert error', upErr)
          const { data: p, error: profErr } = await supabase
            .from('profiles')
            .select('id,email,is_admin,role,phone')
            .eq('id', u.id)
            .maybeSingle()
          if (profErr) console.error('[Auth] profiles fetch error', profErr)
          setProfile(p || null)
        } else {
          setProfile(null)
        }
      })
      sub = r.data
    }
    return () => {
      mounted = false
      if (sub?.subscription) sub.subscription.unsubscribe()
    }
  }, [])

  const signIn = async ({ email, password }) => {
    if (customAuth && loginEndpoint) {
      const resp = await fetch(loginEndpoint, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password })
      })
      const json = await resp.json().catch(()=>({}))
      if (!resp.ok) return { data: null, error: new Error(json?.error || 'Login failed') }
      localStorage.setItem('auth_token', json.token)
      setUser({ id: json.user.id, email: json.user.email })
      setProfile(json.user)
      return { data: { session: { user: { id: json.user.id, email: json.user.email } } }, error: null }
    }
    return await supabase.auth.signInWithPassword({ email, password })
  }

  const signUp = async ({ email, password, phone }) => {
    if (customAuth && signupEndpoint) {
      const resp = await fetch(signupEndpoint, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password, phone })
      })
      const json = await resp.json().catch(()=>({}))
      if (!resp.ok) return { data: null, error: new Error(json?.error || 'Signup failed') }
      localStorage.setItem('auth_token', json.token)
      setUser({ id: json.user.id, email: json.user.email })
      setProfile(json.user)
      return { data: { user: { id: json.user.id, email: json.user.email } }, error: null }
    }
    return await supabase.auth.signUp({ email, password })
  }

  const signOut = async () => {
    console.log('[Auth] signOut start')
    try {
      if (customAuth) {
        localStorage.removeItem('auth_token')
      } else {
        // Only local sign out to prevent loops
        await supabase.auth.signOut({ scope: 'local' })
        console.log('[Auth] local signOut done (scope: local)')
      }

      // Hard-clear any Supabase auth tokens from localStorage
      try {
        const removed = []
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i)
          if (k && k.startsWith('sb-') && (k.endsWith('-auth-token') || k.includes('-auth-token'))) {
            removed.push(k)
          }
        }
        removed.forEach(k => localStorage.removeItem(k))
        if (removed.length) console.log('[Auth] removed localStorage keys:', removed)
      } catch (e) {
        console.warn('[Auth] localStorage cleanup skipped', e)
      }
    } catch (e) {
      console.error('[Auth] signOut exception', e)
    } finally {
      setUser(null)
      setProfile(null)
      console.log('[Auth] local state cleared after signOut')
    }
  }

  return (
    <AuthContext.Provider value={{ user, profile, isAdmin, isOwner, canViewHidden, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}
